import {
  IMultipleRowActions,
  Modal,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IPatientEncounterPsTransferRequest } from "../../../../models/patient-encounter-ps-transfer-request";
import { reprocessPatientEncounterPsTransferRequests } from "../../../../services/patientEncounterPsTransferRequest.service";
import MainApi from "../../../../services/mainApi.service";
import PatientEncounterPsTransferRequestDetailsModal from "./PatientEncounterPsTransferRequestDetailsModal";
import MacroFilter, { IFilterParams, getDefaultPeriodRange } from "./MacroFilter";

const PATIENT_ENCOUNTER_PS_TRANSFER_REQUEST_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/PatientEncounterPsTransferRequest/Search";

const patientEncounterPsTransferRequestService =
  new MainApi<IPatientEncounterPsTransferRequest>(
    PATIENT_ENCOUNTER_PS_TRANSFER_REQUEST_ROUTE,
  );

const MANUAL_REVIEW_STATUS = "MANUAL_REVIEW";

// Copia a data antes de ajustar a hora: o estado do filtro não pode ser alterado por quem só o lê.
const toDateParam = (date: Date | null) =>
  date
    ? new Date(new Date(date).setHours(15)).toISOString().substring(0, 10)
    : undefined;

const translationPath = "page.4uBus.logs.patientEncounterPsTransferRequest.";

const PatientEncounterPsTransferRequest = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IPatientEncounterPsTransferRequest | null>(null);

  const { setMessage } = useNotificationContext();

  const [filterParams, setFilterParams] = useState<IFilterParams>(() => {
    const { start, end } = getDefaultPeriodRange();

    return {
      CreatedStart: start,
      CreatedEnd: end,
      OriginalPatientEncounterNumber: "",
      NewPatientEncounterNumber: "",
      NewPrescriptionNumber: "",
      IndividualBeneficiaryIntegrationId: "",
      IndividualBeneficiaryName: "",
    };
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { sendRequest: reprocessRequest } = useFetch(
    reprocessPatientEncounterPsTransferRequests,
  );

  const statusConfig = useMemo(
    () =>
      ({
        PENDING: { color: "info" as const, key: "pending" },
        PROCESSING: { color: "warning" as const, key: "processing" },
        SUCCESS: { color: "success" as const, key: "success" },
        MANUAL_REVIEW: { color: "error" as const, key: "manual_review" },
      }) as Record<string, { color: "info" | "warning" | "success" | "error"; key: string }>,
    [],
  );

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) return "-";

      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";

      return format(date, "Pp", {
        locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
      }).replaceAll(",", "");
    },
    [language],
  );

  const columns: GridColDef<IPatientEncounterPsTransferRequest>[] = [
    {
      field: "originalPatientEncounterNumber",
      headerName:
        t(translationPath + "original_patient_encounter_number") ||
        "Nº Atendimento Original",
      flex: 1,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "newPatientEncounterNumber",
      headerName:
        t(translationPath + "new_patient_encounter_number") ||
        "Nº Atendimento Novo (PA)",
      flex: 1,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "newPrescriptionNumber",
      headerName:
        t(translationPath + "new_prescription_number") ||
        "Nº Prescrição Nova (PA)",
      flex: 1,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "individualBeneficiaryName",
      headerName: t(translationPath + "individual_beneficiary_name") || "Beneficiário",
      flex: 1.2,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "statusSend",
      headerName: t(translationPath + "status") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";
        const config = statusConfig[params.value] ?? {
          color: "info" as const,
          key: "pending",
        };

        return (
          <Chip
            label={t(translationPath + `status_values.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.6,
      renderCell: (params) => params.value ?? 0,
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      onRefreshHandler();
    },
    [onRefreshHandler],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleReprocess = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;

      const { success } = await reprocessRequest({ ids });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(translationPath + "reprocessedSuccess"),
          type: "success",
        });
      }
    },
    [onRefreshHandler, reprocessRequest, setMessage, t],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (ids, api) => {
          const reprocessableIds = ids.filter((id) => {
            const row = api.current.getRow(
              id,
            ) as IPatientEncounterPsTransferRequest | null;
            return row?.statusSend === MANUAL_REVIEW_STATUS;
          });

          await handleReprocess(reprocessableIds.map((id) => String(id)));
        },
        icon: <ReplayIcon fontSize="small" />,
        text: t(translationPath + "reprocess_selected") || "Reprocessar selecionados",
      },
    ],
    [handleReprocess, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <PatientEncounterPsTransferRequestDetailsModal
            patientEncounterPsTransferRequest={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>

      <Table<IPatientEncounterPsTransferRequest>
        title={t("components.menu.4ubus.logs.patientEncounterPsTransferRequest")}
        columns={columns}
        service={patientEncounterPsTransferRequestService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Pesquisar..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        getRowId={(row) => row.id}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          params.row.statusSend === MANUAL_REVIEW_STATUS
            ? {
                action: () => handleReprocess([params.row.id]),
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "reprocess") || "Reprocessar",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            CreatedStart: toDateParam(filterParams.CreatedStart),
            CreatedEnd: toDateParam(filterParams.CreatedEnd),
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default PatientEncounterPsTransferRequest;
